import { CommonModule } from '@angular/common'
import { Component, computed, effect, inject, input, output, signal } from '@angular/core'
import { AuthService } from '@auth/services/auth.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { Workspace } from '@workspaces/interfaces/workspace.interface'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { MemberAvatar } from '../../types/organization.type'
import { V2TranslationService } from '../../../services/translation.service'

@Component({
  selector: 'app-organization-card',
  standalone: true,
  imports: [CommonModule, TranslateModule, NgxTippyModule],
  templateUrl: './organization-card.component.html',
  styleUrls: []
})
export class OrganizationCardComponent {
  public memberAvatars = computed(() => this._memberAvatars())
  public totalMemberCount = computed(() => this._totalMemberCount())
  // Input signals
  public organization = input.required<Workspace>()
  public isSelected = input<boolean>(false)
  public showManagedBy = input<boolean>(true)
  public lastUsedAt = input<Date | string>()

  // Output signals
  public cardClick = output<Workspace>()
  public cardSelect = output<Workspace>()

  public _translate = inject(TranslateService)
  public _translationService = inject(V2TranslationService)
  private _authService = inject(AuthService)

  // Signals for reactive state management
  private _memberAvatars = signal<MemberAvatar[]>([])
  private _totalMemberCount = signal<number>(0)

  // Computed properties

  constructor() {
    // Effect to initialize member avatars when organization changes
    effect(() => {
      this._initializeMemberAvatars()
    })
  }

  /**
   * @description Handles card click event
   * @returns void
   */
  public onCardClick(): void {
    this.cardClick.emit(this.organization())
    this.cardSelect.emit(this.organization())
  }

  /**
   * Gets last used text
   * @returns string - Formatted last used text
   */
  public getLastUsedText(): string {
    // Use the last_use_diff_to_human field from the API if available
    if (this.organization().last_use_diff_to_human) {
      return `${this._translate.instant('v2.organizationCard.lastUsed')} ${this.organization().last_use_diff_to_human}`
    }

    // Fallback to "Never used" if no human-readable text is available
    return this._translate.instant('v2.organizationCard.neverUsed')
  }

  /**
   * Checks if current view is mobile
   * @returns boolean - True if mobile view
   */
  public isMobile(): boolean {
    return window.innerWidth < 768 // md breakpoint
  }

  /**
   * Checks if this organization is the current one using AuthService
   * @returns boolean - True if this is the current organization
   */
  public isCurrentOrganization(): boolean {
    return this._authService.currentWorkspace?.id === this.organization().id
  }

  /**
   * Gets initials from name
   * @param name - Full name
   * @returns string - Initials
   */
  public getInitials(name: string | null | undefined): string {
    if (!name || typeof name !== 'string') return '??'

    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  /**
   * Gets the upcoming events count for display
   * @returns number - Upcoming events count (0 if not available)
   */
  public getUpcomingEventsCount(): number {
    return this.organization().upcoming_events_count || 0
  }

  /**
   * Gets the live events count for display
   * @returns number - Live events count (0 if not available)
   */
  public getLiveEventsCount(): number {
    return this.organization().live_events_count || 0
  }

  /**
   * Gets tooltip text for event badges
   * @param eventType - Event type ('live' or 'upcoming')
   * @returns string - Tooltip text
   */
  public getEventTooltip(eventType: 'live' | 'upcoming'): string {
    return eventType === 'live' ? this._translate.instant('v2.organizationCard.liveEvents') : this._translate.instant('v2.organizationCard.upcomingEvents')
  }

  /**
   * Initializes member avatars from organization data
   * Processes only first 5 users for display but keeps full count for overflow calculation
   * @returns void
   */
  private _initializeMemberAvatars(): void {
    // Use workspace users from the organization data
    if (this.organization().workspace_users && this.organization().workspace_users.length > 0) {
      // Filter all valid users first to get accurate count
      const allValidUsers = this.organization().workspace_users.filter((workspaceUser) => workspaceUser && workspaceUser.id && workspaceUser.user)

      // Set total count for overflow calculation
      this._totalMemberCount.set(allValidUsers.length)

      // Process only first 5 users for display (performance optimization)
      const members: MemberAvatar[] = allValidUsers
        .slice(0, 5) // Take first 5 users for display
        .map((workspaceUser) => {
          const user = workspaceUser.user!

          // Determine user name with fallback logic: full_name -> email -> 'Unknown User'
          let userName = 'Unknown User'
          let userMail = null
          if (user.full_name && user.full_name.trim()) {
            userName = user.full_name
          } else if (user.email && user.email.trim()) {
            // Use first letter of email if no full_name but email exists
            userName = user.email.charAt(0).toUpperCase()
            userMail = user.email
          }

          return {
            id: user.id,
            full_name: userName,
            email: userMail,
            avatar:
              user.profile_picture || user.avatar?.cropped_src || user.avatar?.original_src
                ? {
                    name: userName || 'User Avatar',
                    cropped_src: user.profile_picture || user.avatar?.cropped_src || user.avatar?.original_src,
                    original_src: user.profile_picture || user.avatar?.original_src || user.avatar?.original_src
                  }
                : null,
            initials: this.getInitials(userName === 'Unknown User' ? null : userName)
          }
        })

      this._memberAvatars.set(members)
    } else {
      // Fallback to empty array if no workspace users
      this._memberAvatars.set([])
      this._totalMemberCount.set(0)
    }
  }
}
